import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Button,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import httpRequest from "../utils/httpRequest";
import html2canvas from "html2canvas";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";

const CreditNoteInvoiceTable = ({ invoices, setPage, setPageSize, pageSize, page }) => {
  const [open, setOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setPage(1);
  };

  const viewPdf = async (credit_note_number) => {
    try {
      setLoadingInvoiceId(credit_note_number);
      const invoiceHtml = await httpRequest(
        "credit_note/generateHtmlForCNInvoice",
        "GET",
        {},
        {
          credit_note_number: credit_note_number,
        }
      );

      if (!invoiceHtml) {
        console.error("Failed to fetch invoice HTML");
        setLoadingInvoiceId(null);
        return null;
      }

      const tempElement = document.createElement("div");
      tempElement.innerHTML = invoiceHtml;
      tempElement.style.position = "fixed";
      tempElement.style.left = "-9999px";
      document.body.appendChild(tempElement);

      await Promise.all(
        Array.from(tempElement.querySelectorAll("img")).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = img.onerror = resolve;
          });
        })
      );

      const canvas = await html2canvas(tempElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginX = 5;
      const marginY = 5;
      const imgWidth = pageWidth - marginX * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", marginX, marginY, imgWidth, imgHeight);

      const now = new Date();
      const dateTimeString = now
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "_")
        .slice(0, -5);
      const fileName = `${credit_note_number}_${dateTimeString}.pdf`;

      const blob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);

      document.body.removeChild(tempElement);

      setLoadingInvoiceId(null);
      return blobUrl;
    } catch (error) {
      console.error("Error generating PDF:", error);
      setLoadingInvoiceId(null);
      return null;
    }
  };

  const handleOpen = async (invoice) => {
    const pdfUrl = await viewPdf(invoice.credit_note_number);
    if (pdfUrl) {
      setSelectedPdfUrl(pdfUrl);
      setSelectedInvoice(invoice);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPdfUrl(null);
    setSelectedInvoice(null);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="invoice table">
          <TableHead>
            <TableRow>
              <TableCell>CN Number</TableCell>
              <TableCell>Issued Against</TableCell>
              <TableCell>Invoice Date</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell>GSTIN</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>PDF</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.data.length ? (
              invoices.data.map((invoice) => (
                <TableRow key={invoice.credit_note_number}>
                  <TableCell>{invoice.credit_note_number}</TableCell>
                  <TableCell>
                    {invoice.invoice_numbers.join(", ")}
                  </TableCell>
                  <TableCell>
                    {new Date(invoice.issue_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{invoice.grand_total}</TableCell>
                  <TableCell>{invoice.firm_name} ({invoice.account_number})</TableCell>
                  <TableCell>{invoice.gst_number}</TableCell>
                  <TableCell>{invoice.type}</TableCell>
                  <TableCell>{invoice.reason}</TableCell>

                  <TableCell>
                    <button
                      onClick={() => handleOpen(invoice)}
                      style={{
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        color: "#555",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        transition: "transform 0.1s ease, opacity 0.2s ease",
                      }}
                      onMouseDown={(e) =>
                        (e.currentTarget.style.transform = "scale(0.85)")
                      }
                      onMouseUp={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      {loadingInvoiceId === invoice.credit_note_number ? (
                        <CircularProgress size={20} />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>No invoices found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <FormControl size="small">
          <InputLabel id="page-size-label">Rows</InputLabel>
          <Select
            labelId="page-size-label"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Rows"
          >
            {[5, 10, 20, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Pagination
          count={invoices.pagination?.totalPages || 1}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Typography variant="h6">
            Credit Note Preview - {selectedInvoice?.credit_note_number}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href={selectedPdfUrl}
            download={`${selectedInvoice?.credit_note_number}_${new Date()
              .toISOString()
              .replace(/[:.]/g, "-")
              .replace("T", "_")
              .slice(0, -5)}.pdf`}
            sx={{ ml: 2, whiteSpace: "nowrap" }}
            startIcon={<DownloadIcon />}
          >
            Download PDF
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPdfUrl && (
            <>
              <Box mb={2} textAlign="right"></Box>
              <iframe
                src={selectedPdfUrl}
                title="PDF Preview"
                width="100%"
                height="600px"
                style={{ border: "none" }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreditNoteInvoiceTable;
